import React, { useMemo, useState } from "react";
import {
  Box,
  Flex,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import PieChart from "components/charts/PieChart";
import { pieChartOptions } from "variables/charts";
import type { WineSalesMonthly } from "types/fulfillment";
import { formatCurrency } from "utils/formatCurrency";
import {
  winePieSegmentsFromMonthly,
  type WinePiePeriod,
} from "utils/winePieFromMonthly";

export type PieCardProps = {
  wineSalesMonthly: WineSalesMonthly | null;
  loading?: boolean;
};

function periodFromSelect(v: string): WinePiePeriod {
  return v === "monthly" ? "lastMonth" : "last6Months";
}

export default function PieCard({
  wineSalesMonthly,
  loading,
  ...rest
}: PieCardProps & Record<string, unknown>) {
  const [range, setRange] = useState("semester");
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const cardColor = useColorModeValue("white", "navy.700");
  const cardShadow = useColorModeValue(
    "0px 18px 40px rgba(112, 144, 176, 0.12)",
    "unset"
  );
  const muted = useColorModeValue("secondaryGray.600", "secondaryGray.600");

  const segments = useMemo(() => {
    if (!wineSalesMonthly) return [];
    return winePieSegmentsFromMonthly(
      wineSalesMonthly,
      periodFromSelect(range)
    );
  }, [wineSalesMonthly, range]);

  const chartOptions = useMemo(
    () => ({
      ...pieChartOptions,
      labels: segments.map((s) => s.name),
      colors: segments.map((s) => s.color),
      fill: { colors: segments.map((s) => s.color) },
      tooltip: {
        ...pieChartOptions.tooltip,
        y: {
          formatter: (val: number) => formatCurrency(val),
        },
      },
    }),
    [segments]
  );

  const pieSeries = segments.map((s) => s.value);

  return (
    <Card p="20px" alignItems="center" flexDirection="column" w="100%" {...rest}>
      <Flex
        px={{ base: "0px", "2xl": "10px" }}
        justifyContent="space-between"
        alignItems="center"
        w="100%"
        mb="8px"
      >
        <Text color={textColor} fontSize="md" fontWeight="600" mt="4px">
          Distribuição por tipo
        </Text>
        <Select
          fontSize="sm"
          variant="subtle"
          width="unset"
          fontWeight="700"
          value={range}
          onChange={(e) => setRange(e.target.value)}
        >
          <option value="monthly">Último mês</option>
          <option value="semester">Últimos 6 meses</option>
        </Select>
      </Flex>

      <Box h="200px" w="100%">
        {loading || !wineSalesMonthly ? (
          <Flex h="100%" align="center" justify="center">
            <Spinner color="brand.500" />
          </Flex>
        ) : segments.length === 0 ? (
          <Flex h="100%" align="center" justify="center" px={4}>
            <Text fontSize="sm" color={muted} textAlign="center">
              Sem vendas neste período para mostrar por tipo de vinho.
            </Text>
          </Flex>
        ) : (
          <PieChart
            h="100%"
            w="100%"
            chartData={pieSeries}
            chartOptions={chartOptions}
          />
        )}
      </Box>

      {segments.length > 0 ? (
        <Card
          bg={cardColor}
          flexDirection="column"
          boxShadow={cardShadow}
          w="100%"
          p="15px"
          px="20px"
          mt="15px"
          mx="auto"
        >
          <SimpleGrid
            columns={{ base: 2, md: 3 }}
            spacingX={{ base: 4, md: 6 }}
            spacingY={4}
            w="100%"
          >
            {segments.map((s) => (
              <Flex key={s.name} direction="column" py="4px">
                <Flex align="center" mb="5px">
                  <Box
                    h="8px"
                    w="8px"
                    bg={s.color}
                    borderRadius="50%"
                    me="4px"
                    flexShrink={0}
                  />
                  <Text
                    fontSize="xs"
                    color={muted}
                    fontWeight="700"
                    noOfLines={1}
                    title={s.name}
                  >
                    {s.name}
                  </Text>
                </Flex>
                <Text fontSize="lg" color={textColor} fontWeight="700">
                  {s.percent.toLocaleString("pt-BR", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 1,
                  })}
                  %
                </Text>
                <Text fontSize="xs" color={muted} mt="2px">
                  {formatCurrency(s.value)}
                </Text>
              </Flex>
            ))}
          </SimpleGrid>
        </Card>
      ) : null}
    </Card>
  );
}
